// #1794 step 3 — assign-page status colours, fetched ONCE per page load.
//
// WHY A MODULE-LEVEL CACHE AND NOT A COMPONENT FETCH.
// `dynamicColumns.vue` is instantiated per EMPLOYEE per COLUMN — `v-for employee`
// wrapping `v-for item in callAssigmentSettings`. At 150 employees and ~15
// configured columns that is ~2,250 instances on one page. A fetch in created()
// would be ~2,250 requests for the same 20 rows, on the very page #1656's
// batching exists to protect.
//
// Memoising the PROMISE rather than the result matters: concurrent mounts share
// one in-flight request instead of each starting their own before any resolves.
import service from './services.js';

const cache = {};

/**
 * Resolve a client's status-colour map. One network request per client per page
 * load; every later caller receives the same promise.
 *
 * Resolves to { byStatus, fallbacks, aliases } or NULL. Null means "no config available",
 * and callers must fall back to the built-in defaults — never to a blank pill.
 */
export function loadStatusColors(clientId, headers) {
    if (!clientId) return Promise.resolve(null);

    if (!cache[clientId]) {
        cache[clientId] = service
            .get('client-status-color', headers)
            .then((res) => {
                const r = (res && res.data && res.data.result) || {};
                const byStatus = {};
                (r.colors || []).forEach((c) => {
                    if (c && c.status) byStatus[c.status] = c;
                });
                // #1868 — `aliases` maps a status with no row of its own onto the
                // row it borrows (booked_confirmed_local -> confirmed_here). Read
                // from the API, never transcribed here.
                return { byStatus, fallbacks: r.fallbacks || {}, aliases: r.aliases || {} };
            })
            .catch(() => {
                // EVICT on rejection. Caching a rejected promise would make one
                // transient failure permanent for the life of the page.
                delete cache[clientId];
                // Null, not a throw: a colour lookup failing must degrade to the
                // built-in palette, never blank the grid.
                return null;
            });
    }

    return cache[clientId];
}

/**
 * Drop a client's cached colours so the next page picks up a saved change.
 *
 * PRODUCTION API, not a test seam — it was originally written as one, under a
 * double-underscore name, and wired to nothing. That is what made the cycle
 * time so bad: a rep saved a colour, the row updated in the database, and the
 * grid kept serving the memoised fetch until a full page reload dropped the JS
 * module. Renamed off the `__` convention so nobody deletes it as test-only.
 *
 * Called after a successful save in configStatusColors.vue. It does NOT refetch —
 * it only forgets, so the next component that needs the colours issues exactly one
 * request. That keeps the memoisation intact: invalidation and the one-fetch
 * guarantee are not in tension, because the cost is paid once on the next page,
 * not once per instance.
 *
 * Module state persists across Vue ROUTES, which is why this works for
 * Config -> Assign with no reload — and equally why, without it, the stale value
 * survived a route change too.
 *
 * LIMIT, stated rather than implied: module state is per TAB. A save in one tab
 * does not reach a second tab that already holds the old value. There is no
 * cross-tab invalidation here and none is claimed.
 *
 * @param {string} [clientId] omit to forget every client
 */
export function invalidateStatusColors(clientId) {
    if (clientId) {
        delete cache[clientId];
        return;
    }
    Object.keys(cache).forEach((k) => delete cache[k]);
}
